import React from "react";
import { createPortal } from "react-dom";
import {
  Modal,
  Form,
  ButtonToolbar,
  Notification,
  useToaster,
  Schema,
  InputGroup,
} from "rsuite";
import EyeIcon from "@rsuite/icons/legacy/Eye";
import EyeSlashIcon from "@rsuite/icons/legacy/EyeSlash";
import PhoneInput from "react-phone-number-input/min";
import "react-phone-number-input/style.css";

import { Translation } from "react-i18next";
import i18n from "i18n";

import { OAuth2Service, UserService } from "_services";
import { authenticationAction } from "_actions";

import { connect } from "react-redux";
import Emitter from "_actions/emitter";
import { BaseServiceURLs } from "BaseServiceURLs";
import { SiteFeatures } from "config/siteFeatures";

const isMobile = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || window.innerWidth <= 768);

// Tarayıcı dilinden (ör. "de-DE") ülke kodunu çıkarır; bulunamazsa/geçersizse
// undefined döner ve PhoneInput hiçbir ülke seçili olmadan (uluslararası simge) başlar.
const getDefaultPhoneCountry = () => {
  if (typeof navigator === "undefined") return undefined;
  const lang = navigator.language || navigator.userLanguage || "";
  const region = lang.split("-")[1];
  return region && /^[A-Za-z]{2}$/.test(region) ? region.toUpperCase() : undefined;
};

const DEFAULT_PHONE_COUNTRY = getDefaultPhoneCountry();

// rsuite Form.Control accepter: PhoneInput'un value/onChange imzası zaten
// rsuite'in beklediğiyle (value, onChange(value)) uyumlu olduğundan, bu şekilde
// entegre etmek Form'un zorunlu-alan hata rozetini (diğer alanlarla aynı) otomatik gösterir.
const PhoneFieldAccepter = React.forwardRef(
  ({ value, onChange, onKeyDown, onFocus, ...rest }, ref) => (
    <div
      ref={ref}
      className="fibbet-phone-input input input-bordered w-full max-w outline-primary-500 flex items-center gap-1.5"
    >
      <PhoneInput
        {...rest}
        international
        limitMaxLength
        defaultCountry={DEFAULT_PHONE_COUNTRY}
        countryCallingCodeEditable={false}
        value={value}
        onChange={(v) => onChange(v || "")}
        className="w-full"
        numberInputProps={{
          onKeyDown,
          onFocus,
          autoComplete: "off",
          className: "bg-transparent outline-none border-none w-full text-inherit",
        }}
      />
    </div>
  )
);


export const LoginModal = (props) => {
  const toaster = useToaster();
  const loginModel = Schema.Model({
    username: Schema.Types.StringType().isRequired(i18n.t("MSG_NULL_USERNAME")),
    password: Schema.Types.StringType().isRequired(i18n.t("MSG_NULL_PASSWORD")),
  });
  const registerModel = Schema.Model({
    FirstName: Schema.Types.StringType().isRequired("First name is required"),
    LastName: Schema.Types.StringType().isRequired("Last name is required"),
    UserName: Schema.Types.StringType().isRequired(i18n.t("MSG_NULL_USERNAME")),
    Email: Schema.Types.StringType().isRequired("Email is required").isEmail("Invalid email"),
    Phone: Schema.Types.StringType().isRequired(i18n.t("MSG_NULL_PHONE")),
    Password: Schema.Types.StringType().isRequired(i18n.t("MSG_NULL_PASSWORD")),
  });
  const formRef = React.useRef();
  const modalBodyRef = React.useRef();
  const mobileScrollRef = React.useRef(null);
  const turnstileContainerRef = React.useRef(null);
  const turnstileWidgetIdRef = React.useRef(null);
  const [viewportHeight, setViewportHeight] = React.useState(
    typeof window !== "undefined" ? window.innerHeight : 600
  );
  const [keyboardOpen, setKeyboardOpen] = React.useState(false);
  const [mode, setMode] = React.useState("login");
  const [formError, setFormError] = React.useState({});
  const [formValue, setFormValue] = React.useState({
    username: "",
    password: "",
  });
  const [registerValue, setRegisterValue] = React.useState({
    FirstName: "",
    LastName: "",
    UserName: "",
    Email: "",
    Phone: "",
    Password: "",
    PublicGamerSettingsModelId: BaseServiceURLs.DEFAULT_PUBLIC_GAMER_SETTINGS_ID,
    CaptchaKey: "",
  });
  const [loading, setLoading] = React.useState(false);
  const [showLoginPassword, setShowLoginPassword] = React.useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = React.useState(false);

  const checkToken = async () => {
    const { LoginSuccess, LoginFailure } = props;
    await UserService.USER_DETAIL()
      .then((res) => {
        LoginSuccess(res);
        Emitter.emit("LOGIN_MODAL", false);
      })
      .catch((err) => {
        LoginFailure();
      });
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!formRef.current.check()) {
      setLoading(false);
      return;
    }
    await OAuth2Service.LOGIN(formValue.username, formValue.password, false)
      .then((res) => {
        toaster.push(message(i18n.t("MESSAGE_LOGIN_SUCCUESS"), "success"), {
          duration: 2000,
        });
        checkToken();
        setLoading(false);
      })
      .catch((err) => {
        if (err) {
          toaster.push(message(i18n.t("MESSAGE_LOGIN_FAILD"), "error"), {
            duration: 2000,
          });
        } else {
          toaster.push(
            message(i18n.t("ERROR_RESPONSE_ERROR_UNDEFINED"), "error"),
            { duration: 2000 }
          );
        }
        setLoading(false);
      });
  };

  const handleRegisterSubmit = async () => {
    setLoading(true);
    if (!formRef.current.check()) {
      setLoading(false);
      return;
    }
    if (!registerValue.CaptchaKey || !registerValue.CaptchaKey.trim()) {
      toaster.push(
        message(i18n.t("MSG_COMPLETE_CAPTCHA") || "Please complete the captcha verification.", "error"),
        { duration: 3000 }
      );
      setLoading(false);
      return;
    }
    await UserService.REGISTER(registerValue)
      .then((res) => {
        toaster.push(
          message(i18n.t("MESSAGE_REGISTER_SUCCESS") || "Registration successful. You can sign in now.", "success"),
          { duration: 3000 }
        );
        setMode("login");
        setRegisterValue({
          FirstName: "",
          LastName: "",
          UserName: "",
          Email: "",
          Phone: "",
          Password: "",
          PublicGamerSettingsModelId: BaseServiceURLs.DEFAULT_PUBLIC_GAMER_SETTINGS_ID,
          CaptchaKey: "",
        });
        if (typeof window !== "undefined" && window.turnstile && turnstileWidgetIdRef.current != null) {
          try {
            window.turnstile.reset(turnstileWidgetIdRef.current);
          } catch (e) { }
        }
        setLoading(false);
      })
      .catch((err) => {
        debugger;
        const msg = err?.response?.data?.failText[0] || "Registration failed. Please try again.";
        toaster.push(message(msg, "error"), { duration: 3000 });
        setLoading(false);
      });
  };

  const message = (msg, type) => {
    return <Notification type={type} header={msg} closable></Notification>;
  };

  const handleClose = () => {
    setMode("login");
    Emitter.emit("LOGIN_MODAL", false);
  };

  const _handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (mode === "login") handleSubmit();
      else handleRegisterSubmit();
    }
  };

  React.useEffect(() => {
    document.body.classList.toggle("chatwoot-force-hidden", !!props.isActive);
    return () => {
      document.body.classList.remove("chatwoot-force-hidden");
    };
  }, [props.isActive]);

  // Modal açıkken arka plan scroll'unu tamamen kapat (iOS dahil); kapandığında eski haline getir
  React.useEffect(() => {
    if (!props.isActive) return;
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY ?? window.pageYOffset;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyTop = body.style.top;
    const prevBodyLeft = body.style.left;
    const prevBodyRight = body.style.right;
    const prevBodyWidth = body.style.width;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.position = prevBodyPosition;
      body.style.top = prevBodyTop;
      body.style.left = prevBodyLeft;
      body.style.right = prevBodyRight;
      body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [props.isActive]);

  // Mobil overlay: tek scroll container, yükseklik = visualViewport (klavye açıkken küçülür)
  React.useEffect(() => {
    if (!props.isActive || !isMobile()) return;
    const v = window.visualViewport;
    if (!v) return;
    const sync = () => {
      setViewportHeight(v.height);
      setKeyboardOpen(v.height < window.innerHeight * 0.85);
    };
    sync();
    v.addEventListener("resize", sync);
    v.addEventListener("scroll", sync);
    return () => {
      v.removeEventListener("resize", sync);
      v.removeEventListener("scroll", sync);
      setKeyboardOpen(false);
    };
  }, [props.isActive]);

  // Klavye açıkken sadece bizim scroll div kayabilsin; başka element scroll olmasın = siyah alan engeli
  React.useEffect(() => {
    if (!props.isActive || !isMobile()) return;
    const onTouchMove = (e) => {
      const scrollEl = mobileScrollRef.current;
      if (!scrollEl || scrollEl.contains(e.target)) return;
      e.preventDefault();
    };
    const t = setTimeout(() => {
      document.addEventListener("touchmove", onTouchMove, { passive: false });
    }, 50);
    return () => {
      clearTimeout(t);
      document.removeEventListener("touchmove", onTouchMove);
    };
  }, [props.isActive]);

  const handleInputFocus = (e) => {
    const input = e.target;
    if (!input) return;
    const scrollEl = isMobile() ? mobileScrollRef.current : modalBodyRef.current;
    if (!scrollEl) return;
    const v = window.visualViewport || window;
    const vh = v.height ?? window.innerHeight;
    setTimeout(() => {
      const rect = input.getBoundingClientRect();
      const targetScroll = scrollEl.scrollTop + rect.top - vh * 0.2;
      scrollEl.scrollTo({ top: Math.max(0, targetScroll), behavior: "smooth" });
    }, 350);
  };

  // Cloudflare Turnstile widget: register modunda render et, modal kapanınca veya login'e geçince kaldır
  React.useEffect(() => {
    if (!props.isActive || mode !== "register") return;

    let intervalId = null;
    let timeoutId = null;
    let mounted = true;

    const renderTurnstile = () => {
      const container = turnstileContainerRef.current;
      if (!mounted || !container || !container.isConnected) return;
      if (typeof window === "undefined" || !window.turnstile) return;
      try {
        const widgetId = window.turnstile.render(container, {
          sitekey: BaseServiceURLs.TURNSTILE_SITE_KEY,
          theme: "light",
          size: "normal",
          callback: (token) => {
            if (mounted) setRegisterValue((prev) => ({ ...prev, CaptchaKey: token }));
          },
          "expired-callback": () => {
            if (mounted) setRegisterValue((prev) => ({ ...prev, CaptchaKey: "" }));
          },
          "error-callback": () => {
            if (mounted) setRegisterValue((prev) => ({ ...prev, CaptchaKey: "" }));
          },
        });
        turnstileWidgetIdRef.current = widgetId;
      } catch (e) {
        console.warn("Turnstile render error:", e);
      }
    };

    // turnstile.ready() script async/defer ile kullanılamıyor; doğrudan render çağırıyoruz
    const runWhenReady = () => renderTurnstile();

    const loadScriptIfNeeded = (callback) => {
      if (window.turnstile) {
        callback();
        return;
      }
      const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
      if (existing) {
        intervalId = setInterval(() => {
          if (window.turnstile) {
            clearInterval(intervalId);
            intervalId = null;
            callback();
          }
        }, 100);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.onload = () => setTimeout(callback, 50);
      document.head.appendChild(script);
    };

    // Modal ve container DOM'a yerleştikten sonra render et (Turnstile görünür container ister)
    timeoutId = setTimeout(() => {
      if (!mounted) return;
      const container = turnstileContainerRef.current;
      if (!container) return;

      loadScriptIfNeeded(runWhenReady);
    }, 200);

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
      if (typeof window !== "undefined" && window.turnstile && turnstileWidgetIdRef.current != null) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch (e) { }
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [props.isActive, mode]);

  const renderInnerContent = (t) => (
    <>
      <div className="flex flex-col items-center shadow-xl w-full py-2 pb-8">
        {mode === "login" && (
          <div className="mt-8 mb-4">
            <div className="avatar">
              <div className="w-32">
                <img
                  style={{ objectFit: "contain" }}
                  src={require("../../assets/img/gamebox1.png")}
                />
              </div>
            </div>
          </div>
        )}
        <div className="w-full sm:max-w-md mt-2 px-6 py-4 secondary-950 overflow-hidden sm:rounded-lg">
                  {mode === "login" ? (
                    <Form
                      ref={formRef}
                      fluid
                      model={loginModel}
                      onChange={setFormValue}
                      onCheck={setFormError}
                      formValue={formValue}
                    >
                      <Form.Group>
                        <Form.ControlLabel className="label-text">
                          {t("TXT_USERNAME")}
                        </Form.ControlLabel>
                        <Form.Control
                          onKeyDown={_handleKeyDown}
                          onFocus={handleInputFocus}
                          className="input input-bordered w-full max-w outline-primary-500"
                          name="username"
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.ControlLabel className="label-text">
                          {t("TXT_PASSWORD")}
                        </Form.ControlLabel>
                        <InputGroup inside className="flex items-center">
                          <Form.Control
                            onKeyDown={_handleKeyDown}
                            onFocus={handleInputFocus}
                            className="input input-bordered w-full max-w outline-primary-500"
                            name="password"
                            type={showLoginPassword ? "text" : "password"}
                            autoComplete="off"
                          />
                          <InputGroup.Button
                            onClick={() => setShowLoginPassword((v) => !v)}
                            aria-label={showLoginPassword ? "Hide password" : "Show password"}
                          >
                            {showLoginPassword ? <EyeIcon /> : <EyeSlashIcon />}
                          </InputGroup.Button>
                        </InputGroup>
                      </Form.Group>
                      <Form.Group>
                        <ButtonToolbar>
                          <div
                            className="btn btn-primary btn-md btn-block text-white"
                            onClick={handleSubmit}
                          >
                            {t("BUTTON_SIGNIN")}
                          </div>
                        </ButtonToolbar>
                      </Form.Group>
                      {SiteFeatures.ONLINE_REGISTER && (
                      <div className="text-center mt-2">
                        <button
                          type="button"
                          className={`text-sm text-primary-400 hover:underline ${keyboardOpen ? "pb-36" : "pb-0"}`}
                          onClick={() => setMode("register")}
                        >
                          {t("TXT_REGISTER_LINK") || "Create account"}
                        </button>
                      </div>
                      )}
                    </Form>
                  ) : (
                    <Form
                      ref={formRef}
                      fluid
                      model={registerModel}
                      onChange={setRegisterValue}
                      onCheck={setFormError}
                      formValue={registerValue}
                    >
                      <div className="flex gap-3">
                        <Form.Group className="flex-1">
                          <Form.ControlLabel className="label-text">
                            {t("LABEL_FIRSTNAME")}
                          </Form.ControlLabel>
                          <Form.Control
                            onKeyDown={_handleKeyDown}
                            onFocus={handleInputFocus}
                            className="input input-bordered w-full max-w outline-primary-500"
                            name="FirstName"
                            maxLength={50}
                          />
                        </Form.Group>
                        <Form.Group className="flex-1">
                          <Form.ControlLabel className="label-text">
                            {t("LABEL_LASTNAME")}
                          </Form.ControlLabel>
                          <Form.Control
                            onKeyDown={_handleKeyDown}
                            onFocus={handleInputFocus}
                            className="input input-bordered w-full max-w outline-primary-500"
                            name="LastName"
                            maxLength={50}
                          />
                        </Form.Group>
                      </div>
                      <Form.Group>
                        <Form.ControlLabel className="label-text">
                          {t("TXT_USERNAME")}
                        </Form.ControlLabel>
                        <Form.Control
                          onKeyDown={_handleKeyDown}
                          onFocus={handleInputFocus}
                          className="input input-bordered w-full max-w outline-primary-500"
                          name="UserName"
                          maxLength={30}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.ControlLabel className="label-text">
                          {t("LABEL_EMAIL")}
                        </Form.ControlLabel>
                        <Form.Control
                          onKeyDown={_handleKeyDown}
                          onFocus={handleInputFocus}
                          className="input input-bordered w-full max-w outline-primary-500"
                          name="Email"
                          type="email"
                          maxLength={254}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.ControlLabel className="label-text">
                          {t("LABEL_PHONE")}
                        </Form.ControlLabel>
                        <Form.Control
                          name="Phone"
                          accepter={PhoneFieldAccepter}
                          onKeyDown={_handleKeyDown}
                          onFocus={handleInputFocus}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.ControlLabel className="label-text">
                          {t("TXT_PASSWORD")}
                        </Form.ControlLabel>
                        <InputGroup inside className="flex items-center">
                          <Form.Control
                            onKeyDown={_handleKeyDown}
                            onFocus={handleInputFocus}
                            className="input input-bordered w-full max-w outline-primary-500"
                            name="Password"
                            type={showRegisterPassword ? "text" : "password"}
                            autoComplete="off"
                            maxLength={64}
                          />
                          <InputGroup.Button
                            onClick={() => setShowRegisterPassword((v) => !v)}
                            aria-label={showRegisterPassword ? "Hide password" : "Show password"}
                          >
                            {showRegisterPassword ? <EyeIcon /> : <EyeSlashIcon />}
                          </InputGroup.Button>
                        </InputGroup>
                      </Form.Group>
                      <Form.Group>
                        <Form.ControlLabel className="label-text">
                          {t("TXT_CAPTCHA") || "Captcha"}
                        </Form.ControlLabel>
                        <div
                          ref={turnstileContainerRef}
                          id="login-modal-turnstile"
                          className="flex justify-start mt-1 min-h-[65px] w-full"
                          style={{ minHeight: "65px" }}
                          aria-label="Cloudflare Turnstile"
                        />
                      </Form.Group>
                      <Form.Group>
                        <ButtonToolbar>
                          <div
                            className="btn btn-primary btn-md btn-block text-white"
                            onClick={handleRegisterSubmit}
                          >
                            {t("BUTTON_REGISTER") || "Register"}
                          </div>
                        </ButtonToolbar>
                      </Form.Group>
                      <div className="text-center mt-2">
                        <button
                          type="button"
                          className={`text-sm text-primary-400 hover:underline ${keyboardOpen ? "pb-36" : "pb-0"}`}
                          onClick={() => setMode("login")}
                        >
                          {t("TXT_LOGIN_LINK") || "Already have an account? Sign in"}
                        </button>
                      </div>
                    </Form>
                  )}
                </div>
        <span className="my-2 font-light text-secondary-900/20">{t("VERSION")}</span>
      </div>
    </>
  );

  return (
    <Translation>
      {(t) => {
        if (!props.isActive) return null;
        if (isMobile()) {
          return createPortal(
            <div
              className="login-mobile-overlay"
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1050,
                background: "rgba(0,0,0,0.6)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                touchAction: "none",
              }}
            >
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-ghost btn-sm text-white absolute top-4 right-4 z-10"
                style={{ position: "absolute", top: 16, right: 16, zIndex: 11 }}
                aria-label="Close"
              >
                ✕
              </button>
              <div
                ref={mobileScrollRef}
                className="bg-secondary-500/95 flex-1 overflow-y-auto"
                style={{
                  height: `${viewportHeight}px`,
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "none",
                  touchAction: "pan-y",
                  paddingTop: "max(env(safe-area-inset-top), 1em)",
                  paddingBottom: "1em",
                }}
              >
                {renderInnerContent(t)}
              </div>
            </div>,
            document.body
          );
        }
        return (
          <Modal
            onClose={handleClose}
            className="custom-modal custom-modal-fullscreen"
            backdrop={true}
            open={props.isActive}
            size="xs"
          >
            <Modal.Header className="bg-secondary-500 border-0">
              <Modal.Title className="text-white h-[50px]" />
            </Modal.Header>
            <Modal.Body
              ref={modalBodyRef}
              className="bg-secondary-500/80"
              style={{
                paddingTop: "1em",
                paddingBottom: "1em",
                marginTop: 0,
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                position: "relative",
                maxHeight: "100vh",
              }}
            >
              {renderInnerContent(t)}
            </Modal.Body>
          </Modal>
        );
      }}
    </Translation>
  );
};

const mapStateToProps = (state, props) => {
  const { authentication } = state;
  const { user, loggedIn } = authentication;

  return {
    user,
    loggedIn,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    UpdateUser: (user) => dispatch(authenticationAction.UpdateUser(user)),
    LoginSuccess: (user) => dispatch(authenticationAction.LoginSuccess(user)),
    LoginFailure: () => dispatch(authenticationAction.LoginFailure()),
    logout: () => dispatch(authenticationAction.logout()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginModal);
